/**
 * Choix de l'organisation à gérer. Une seule : on y va directement.
 * L'équipe (is_staff) voit toutes les organisations : recherche par nom.
 */
import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getManagedOrganizations } from '@web/features/auth/services/userApi'
import { readLastManagedOrgId } from '@web/features/auth/utils/lastManagedOrgStorage'
import { UserMenu } from '../components/UserMenu'
import brandLogo from '@web/img/Logo-Refuge-500.png'

export function OrgPickerPage() {
  const { t } = useTranslation('gestion')
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q.trim()), 300)
    return () => window.clearTimeout(id)
  }, [q])

  const { data, isLoading, error } = useQuery({
    queryKey: ['managedOrganizations', debounced],
    queryFn: ({ signal }) => getManagedOrganizations({ signal, q: debounced || undefined }),
  })
  const orgs = data?.organizations ?? []
  const last = readLastManagedOrgId()

  if (!isLoading && !error && !debounced && !data?.is_staff && orgs.length === 1) {
    return <Navigate to={`/o/${orgs[0].id}`} replace />
  }

  return (
    <div className="g-picker">
      <header className="g-picker__top">
        <img src={brandLogo} alt="" className="g-picker__logo" />
        <span className="g-picker__brand">{t('app.titre')}</span>
        <span className="g-spacer" />
        <UserMenu />
      </header>
      <main className="g-picker__main">
        <h1 className="g-page__title">{t('choix.titre')}</h1>
        {(data?.is_staff || orgs.length > 8 || debounced) && (
          <input
            className="g-input g-picker__search"
            type="search"
            placeholder={t('choix.rechercher')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
        )}
        {isLoading && <p className="g-muted">{t('commun.chargement')}</p>}
        {error && <p className="g-alert g-alert--error">{(error as Error).message}</p>}
        {!isLoading && !error && orgs.length === 0 && (
          <div className="g-empty">
            <span className="mdi mdi-domain-off g-empty__icon" aria-hidden />
            <p>{debounced ? t('choix.aucun_resultat') : t('choix.aucune')}</p>
          </div>
        )}
        <ul className="g-picker__list">
          {orgs.map((o) => (
            <li key={o.id}>
              <Link to={`/o/${o.id}`} className="g-picker__item">
                {o.logo_url ? (
                  <img src={o.logo_url} alt="" className="g-picker__item-logo" />
                ) : (
                  <span className="g-picker__item-logo g-picker__item-logo--empty mdi mdi-pine-tree" aria-hidden />
                )}
                <span className="g-picker__item-text">
                  <span className="g-picker__item-name">{o.name}</span>
                  <span className="g-muted">
                    {o.organization_type ?? ''}
                    {o.is_directly_managed === false ? ` · ${t('choix.heritee')}` : ''}
                  </span>
                </span>
                {o.id === last && <span className="g-badge">{t('choix.derniere')}</span>}
                <span className="mdi mdi-chevron-right" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  )
}
